
import { Component, Vue, Prop, Emit, Watch } from "vue-property-decorator";
import draggable from "vuedraggable";
import { ExportApi, Header, NewTaskCandReq } from "@/api/export";
import Auth from "@/lib/common/auth";

interface ExportFormData {
  fileName: string;
  fileType: "xlsx" | "csv";
}

interface GroupedHeader {
  key: string;
  name: string;
  fields: Header[];
}

@Component({
  components: {
    draggable
  }
})
export default class ExportDataDrawer extends Vue {
  @Prop({ default: false })
  visible!: boolean;

  @Prop({ default: () => [] })
  selectedList!: any[];

  @Prop({ default: 0 })
  candidateIdsLength!: number;

  @Prop({ default: () => ({}) })
  candidateListForm!: any;

  @Prop({ default: () => [] })
  preloadedHeaders!: any[];

  @Prop({ default: "selected" })
  exportMode!: "selected" | "all";

  // 缓存相关常量
  private readonly EXPORT_DATA_FIELDS_CACHE_KEY = "AIHR_EXPORT_DATA_FIELDS_CACHE";
  private readonly EXPORT_DATA_FIELDS_ORDER_CACHE_KEY =
    "AIHR_EXPORT_DATA_FIELDS_ORDER_CACHE";

  // 数据状态
  headers: Header[] = [];
  leftSearchKey = "";
  rightSearchKey = "";
  activeGroup = "";
  groupPositions: Record<string, number> = {};
  isClickScrolling = false;
  confirmLoading = false;

  // 加载状态管理
  groupToggleLoading: Record<string, boolean> = {};
  clearAllLoading = false;

  // 进度提示文本
  groupProgressText: Record<string, string> = {};
  clearAllProgressText = "";

  // 表单数据
  formData: ExportFormData = {
    fileName: "",
    fileType: "xlsx"
  };

  // 表单验证规则
  rules = {
    fileName: [{ required: true, message: "请输入文件名", trigger: "blur" }]
  };

  get selectedCount() {
    return this.selectedList.length || this.candidateIdsLength;
  }

  get showDifferentExamWarning() {
    // 仅在选中导出模式下判断
    if (this.exportMode !== "selected") {
      return false;
    }

    // 检查选中的候选人是否来自不同面试
    if (this.selectedList.length > 1) {
      const examIds = new Set(
        this.selectedList.map((item: any) => item.examId)
      );
      return examIds.size > 1; // 有多个不同的 examId
    }

    return false;
  }

  // 按组分类的表头数据
  get groupedHeaders(): GroupedHeader[] {
    const grouped: Record<string, Header[]> = {};

    this.headers.forEach(header => {
      const group = header.group || "其他";
      if (!grouped[group]) {
        grouped[group] = [];
      }
      grouped[group].push(header);
    });

    // 固定的分组顺序
    const fixedOrder = ["basic", "param", "evaluate", "detection"];
    const result: GroupedHeader[] = [];

    // 按固定顺序添加分组
    fixedOrder.forEach(key => {
      if (grouped[key]) {
        result.push({
          key,
          name: this.getGroupName(key),
          fields: grouped[key]
        });
      }
    });

    // 添加其他分组（如果有）
    Object.keys(grouped).forEach(key => {
      if (!fixedOrder.includes(key)) {
        result.push({
          key,
          name: this.getGroupName(key),
          fields: grouped[key]
        });
      }
    });

    return result;
  }

  // 过滤后的分组表头
  get filteredGroupedHeaders(): GroupedHeader[] {
    if (!this.leftSearchKey) {
      return this.groupedHeaders;
    }

    return this.groupedHeaders
      .map(group => ({
        ...group,
        fields: group.fields.filter(field =>
          field.headerInExcel
            .toLowerCase()
            .includes(this.leftSearchKey.toLowerCase())
        )
      }))
      .filter(group => group.fields.length > 0);
  }

  // 可见的组（用于左侧导航）
  get visibleGroups() {
    return this.filteredGroupedHeaders;
  }

  // 已选字段（按排序显示）
  get selectedFields(): Header[] {
    return this.headers
      .filter(field => field.on)
      .sort((a, b) => {
        const orderA = (a as any)._sortOrder ?? 999999;
        const orderB = (b as any)._sortOrder ?? 999999;
        return orderA - orderB;
      });
  }

  // 过滤后的已选字段
  get filteredSelectedFields(): Header[] {
    if (!this.rightSearchKey) {
      return this.selectedFields;
    }

    return this.selectedFields.filter(field =>
      field.headerInExcel
        .toLowerCase()
        .includes(this.rightSearchKey.toLowerCase())
    );
  }

  set filteredSelectedFields(value: Header[]) {
    // 拖拽排序时更新 _sortOrder 属性，不修改 headers 数组顺序
    value.forEach((field, index) => {
      this.$set(field, '_sortOrder', index);
    });
  }

  // 总字段数
  get totalFieldCount() {
    return this.headers.length;
  }

  // 检查是否有任何加载操作正在进行
  get isAnyLoading() {
    return (
      this.clearAllLoading ||
      Object.values(this.groupToggleLoading).some(loading => loading)
    );
  }

  // 字段选中状态查询
  isFieldSelected(field: Header): boolean {
    return field.on || false;
  }

  // 切换字段选择状态
  toggleFieldSelection(field: Header, selected: boolean) {
    field.on = selected;
  }

  created() {
    // 初始化
  }

  // 缓存工具方法
  private saveFieldsCache(selectedTypes: string[]) {
    try {
      const cache: Record<string, boolean> = {};
      selectedTypes.forEach(type => {
        cache[type] = true;
      });
      localStorage.setItem(
        this.EXPORT_DATA_FIELDS_CACHE_KEY,
        JSON.stringify(cache)
      );
    } catch (error) {
      // 缓存保存失败，静默处理
      console.warn("保存字段缓存失败:", error);
    }
  }

  private saveFieldsOrderCache(selectedTypes: string[]) {
    try {
      localStorage.setItem(
        this.EXPORT_DATA_FIELDS_ORDER_CACHE_KEY,
        JSON.stringify(selectedTypes)
      );
    } catch (error) {
      // 缓存保存失败，静默处理
      console.warn("保存字段排序缓存失败:", error);
    }
  }

  private getFieldsCache(): Record<string, boolean> | null {
    try {
      const cacheStr = localStorage.getItem(this.EXPORT_DATA_FIELDS_CACHE_KEY);
      return cacheStr ? JSON.parse(cacheStr) : null;
    } catch (error) {
      console.warn("读取字段缓存失败:", error);
      return null;
    }
  }

  private getFieldsOrderCache(): string[] | null {
    try {
      const cacheStr = localStorage.getItem(
        this.EXPORT_DATA_FIELDS_ORDER_CACHE_KEY
      );
      return cacheStr ? JSON.parse(cacheStr) : null;
    } catch (error) {
      console.warn("读取字段排序缓存失败:", error);
      return null;
    }
  }

  @Watch("visible")
  onVisibleChange(newVal: boolean) {
    if (newVal) {
      this.initializeDrawer();
    }
  }

  // 初始化抽屉
  async initializeDrawer() {
    // 重置表单数据
    this.formData = {
      fileName: `候选人综合数据-${new Date()
        .toISOString()
        .slice(0, 10)
        .replace(/-/g, "")}`,
      fileType: "xlsx"
    };

    this.leftSearchKey = "";
    this.rightSearchKey = "";
    this.activeGroup = "";

    // 初始化时重置loading状态和进度文本
    this.clearAllLoading = false;
    this.groupToggleLoading = {};
    this.clearAllProgressText = "";
    this.groupProgressText = {};

    // 使用预加载的表头数据
    if (this.preloadedHeaders && this.preloadedHeaders.length > 0) {
      this.headers = [...this.preloadedHeaders];
    } else {
      this.headers = [];
    }

    // 应用缓存（如果存在）
    this.applyCachedFieldsSelection();

    // 设置默认激活组
    if (this.groupedHeaders.length > 0) {
      this.activeGroup = this.groupedHeaders[0].key;
    }

    this.$nextTick(() => {
      this.updateGroupPositions();
      this.resetScrollPosition();
    });
  }

  // 应用缓存的字段选择
  private applyCachedFieldsSelection() {
    try {
      const fieldsCache = this.getFieldsCache();
      const orderCache = this.getFieldsOrderCache();

      // 如果没有缓存，直接使用后端返回的默认值，不做任何处理
      if (!fieldsCache || !orderCache) {
        return;
      }

      // 有缓存 - 完全覆盖后端的默认选中状态
      this.headers.forEach(header => {
        // 如果缓存中有这个字段，使用缓存值；否则设为 false
        header.on = fieldsCache[header.headerInSql] || false;
      });

      // 应用排序缓存 - 给每个字段添加排序信息
      orderCache.forEach((headerInSql, index) => {
        const header = this.headers.find(h => h.headerInSql === headerInSql);
        if (header && header.on) {
          // 动态添加排序属性（用于右侧已选字段排序）
          this.$set(header, '_sortOrder', index);
        }
      });

      // 给缓存中没有的新字段设置排序（追加到末尾）
      let maxOrder = orderCache.length;
      this.headers.forEach(header => {
        if (header.on && (header as any)._sortOrder === undefined) {
          this.$set(header, '_sortOrder', maxOrder++);
        }
      });

      console.log(
        "缓存应用成功，已选字段数:",
        this.headers.filter(h => h.on).length
      );
    } catch (error) {
      console.warn("应用缓存失败:", error);
      // 缓存应用失败不影响正常使用
    }
  }

  // 构建基础参数
  buildBaseParams(): NewTaskCandReq {
    const form = this.candidateListForm;
    const params: NewTaskCandReq = {
      execType: 0, // 默认值，会被覆盖
      examType: "aibuilder",
      examId: form.examId,
      examVersionId: form.examVersionId,
      subExamId: form.subExamId,
      candidateIdList: this.getCandidateIds(),

      // 从筛选表单获取其他参数
      searchKeyLike: form.searchKeyLike,
      showNameOrPhone: form.showNameOrPhone,
      phone: form.phone,
      positionId: form.positionId,
      tagNameList: form.tagNameList,
      aiRecommendStatusList: form.aiRecommendStatusList,
      submitStatusList: form.submitStatusList,
      auditStatusList: form.auditStatusList,
      createdTime:
        form.createdTime && form.createdTime.length === 2
          ? [form.createdTime[0], form.createdTime[1]]
          : undefined,
      beginTime:
        form.beginTime && form.beginTime.length === 2
          ? [form.beginTime[0], form.beginTime[1]]
          : undefined,
      submitTime:
        form.submitTime && form.submitTime.length === 2
          ? [form.submitTime[0], form.submitTime[1]]
          : undefined,
      paramSearchList: form.paramSearchList,
      afterBuy: form.afterBuy,
      outLinkStatusList: form.outLinkStatusList,
      markCallbackStatusList: form.markCallbackStatusList
    };

    return params;
  }

  // 获取候选人ID列表
  getCandidateIds(): number[] {
    // 全部导出模式返回空数组
    if (this.exportMode === "all") {
      return [];
    }

    // 选中导出模式的原有逻辑
    if (this.candidateIdsLength > 0) {
      return this.candidateListForm.candidateIdList || [];
    }

    if (this.selectedList.length > 0) {
      return this.selectedList.map((item: any) => parseInt(item.candidateId));
    }

    return [];
  }

  // 获取组名称
  getGroupName(key: string): string {
    const nameMap: Record<string, string> = {
      basic: "基础信息",
      param: "登记信息",
      evaluate: "测评结果",
      detection: "系统检测结果",
      其他: "其他"
    };
    return nameMap[key] || key;
  }

  // 🚀 高性能字段选择切换
  handleFieldToggle(field: Header, checked: boolean) {
    this.toggleFieldSelection(field, checked);

    // 更新排序：新选中的字段追加到末尾
    if (checked) {
      const maxOrder = Math.max(
        ...this.selectedFields.map(f => (f as any)._sortOrder ?? 0),
        -1
      );
      this.$set(field, '_sortOrder', maxOrder + 1);
    }
  }

  // 判断组是否全选
  isGroupAllSelected(groupKey: string): boolean {
    const group = this.filteredGroupedHeaders.find(g => g.key === groupKey);
    if (!group || group.fields.length === 0) {
      return false;
    }
    return group.fields.every(field => field.on);
  }

  // 切换组选择状态
  async toggleGroupSelection(groupKey: string) {
    if (this.groupToggleLoading[groupKey] || this.clearAllLoading) {
      return;
    }

    const group = this.filteredGroupedHeaders.find(g => g.key === groupKey);
    if (!group || group.fields.length === 0) {
      return;
    }

    // 性能监控
    const startTime = performance.now();
    console.log(
      `[性能] 组选择开始 - ${groupKey}(${group.fields.length}个字段)`
    );

    this.$set(this.groupToggleLoading, groupKey, true);
    this.$set(this.groupProgressText, groupKey, "准备中...");

    try {
      const allSelected = this.isGroupAllSelected(groupKey);
      const shouldSelect = !allSelected;

      // 计算当前最大排序值
      let maxOrder = Math.max(
        ...this.selectedFields.map(f => (f as any)._sortOrder ?? 0),
        -1
      );

      // 批量更新字段状态 - 分块处理减少响应式压力
      const BATCH_SIZE = 50; // 每批处理50个字段
      const totalFields = group.fields.length;

      this.$set(
        this.groupProgressText,
        groupKey,
        `处理${totalFields}个字段...`
      );

      if (totalFields <= BATCH_SIZE) {
        // 少量字段直接批量处理
        group.fields.forEach(field => {
          field.on = shouldSelect;
          // 新选中的字段分配排序
          if (shouldSelect) {
            this.$set(field, '_sortOrder', ++maxOrder);
          }
        });
      } else {
        // 大量字段分块异步处理
        for (let i = 0; i < totalFields; i += BATCH_SIZE) {
          const batch = group.fields.slice(i, i + BATCH_SIZE);
          batch.forEach(field => {
            field.on = shouldSelect;
            // 新选中的字段分配排序
            if (shouldSelect) {
              this.$set(field, '_sortOrder', ++maxOrder);
            }
          });

          // 进度UI更新
          const processed = Math.min(i + BATCH_SIZE, totalFields);
          const progress = Math.round((processed / totalFields) * 100);
          this.$set(
            this.groupProgressText,
            groupKey,
            `${progress}% (${processed}/${totalFields})`
          );

          // 每批处理后让出控制权，避免阻塞UI
          if (i + BATCH_SIZE < totalFields) {
            await new Promise(resolve => setTimeout(resolve, 0));
          }
        }
      }

      // 等待Vue响应式更新完成
      await this.$nextTick();
    } catch (error) {
      console.error(`[错误] 组选择失败 - ${groupKey}:`, error);
      this.$message.error("操作失败，请稍后重试");
    } finally {
      this.$set(this.groupToggleLoading, groupKey, false);
      this.$set(this.groupProgressText, groupKey, "");

      // 性能监控结果
      const totalTime = performance.now() - startTime;
      console.log(
        `[性能] 组选择完成 - ${groupKey}, 耗时:${totalTime.toFixed(2)}ms`
      );

      // 性能告警
      if (totalTime > 1000) {
        console.warn(
          `[告警] 组选择耗时过长 - ${groupKey}, ${totalTime.toFixed(2)}ms`
        );
      }
    }
  }

  // 清除所有选择
  async clearAllSelected() {
    if (
      this.clearAllLoading ||
      Object.values(this.groupToggleLoading).some(loading => loading)
    )
      return;

    if (this.selectedFields.length === 0) {
      this.$message.info("当前没有已选择的字段");
      return;
    }

    // 性能监控
    const startTime = performance.now();
    const selectedCount = this.selectedFields.length;
    const totalCount = this.headers.length;
    console.log(
      `[性能] 清除所有选择开始 - 已选:${selectedCount}, 总数:${totalCount}`
    );

    this.clearAllLoading = true;
    this.clearAllProgressText = "准备清除...";

    try {
      // 批量清除字段状态 - 分块异步处理提升性能
      const BATCH_SIZE = 100; // 每批处理100个字段
      const totalHeaders = this.headers.length;

      this.clearAllProgressText = `清除${totalHeaders}个字段...`;

      if (totalHeaders <= BATCH_SIZE) {
        // 少量字段直接批量处理
        this.headers.forEach(field => {
          field.on = false;
        });
      } else {
        // 大量字段分块异步处理
        for (let i = 0; i < totalHeaders; i += BATCH_SIZE) {
          const endIndex = Math.min(i + BATCH_SIZE, totalHeaders);

          // 批量处理当前块
          for (let j = i; j < endIndex; j++) {
            this.headers[j].on = false;
          }

          // 进度UI更新
          const progress = Math.round((endIndex / totalHeaders) * 100);
          this.clearAllProgressText = `${progress}% (${endIndex}/${totalHeaders})`;

          // 每批处理后让出控制权，避免阻塞UI
          if (endIndex < totalHeaders) {
            await new Promise(resolve => setTimeout(resolve, 0));
          }
        }
      }

      // 等待Vue响应式更新完成
      await this.$nextTick();
    } catch (error) {
      console.error(`[错误] 清除所有选择失败:`, error);
      this.$message.error("操作失败，请稍后重试");
    } finally {
      this.clearAllLoading = false;
      this.clearAllProgressText = "";

      // 性能监控结果
      const totalTime = performance.now() - startTime;
      const remainingSelected = this.selectedFields.length;
      console.log(
        `[性能] 清除完成 - 耗时:${totalTime.toFixed(
          2
        )}ms, 成功:${remainingSelected === 0}`
      );

      // 性能告警
      if (totalTime > 1000) {
        console.warn(`[告警] 清除操作耗时过长 - ${totalTime.toFixed(2)}ms`);
      }
    }
  }

  // 移除已选字段
  removeSelectedField(field: Header) {
    field.on = false;
  }

  // 拖拽变化处理
  handleDragChange() {
    // 拖拽排序已在 setter 中处理
  }

  // 滚动到指定组
  scrollToGroup(groupKey: string) {
    const container = this.$refs.fieldsContainer as HTMLElement;
    if (!container) return;

    const element = document.querySelector(
      `#field-group-${groupKey}`
    ) as HTMLElement;
    if (element) {
      this.isClickScrolling = true;
      this.activeGroup = groupKey;

      const containerRect = container.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const scrollTop =
        elementRect.top - containerRect.top + container.scrollTop - 5;

      container.scrollTo({
        top: scrollTop,
        behavior: "smooth"
      });

      setTimeout(() => {
        this.isClickScrolling = false;
      }, 500);
    }
  }

  // 更新组位置信息
  updateGroupPositions() {
    const container = this.$refs.fieldsContainer as HTMLElement;
    if (!container) return;

    this.groupedHeaders.forEach(group => {
      const element = document.querySelector(
        `#field-group-${group.key}`
      ) as HTMLElement;
      if (element) {
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        this.groupPositions[group.key] =
          elementRect.top - containerRect.top + container.scrollTop;
      }
    });
  }

  // 滚动处理
  handleScroll() {
    if (this.isClickScrolling) return;

    const container = this.$refs.fieldsContainer as HTMLElement;
    if (!container) return;

    const scrollTop = container.scrollTop;
    const groups = this.groupedHeaders.map(g => g.key);

    for (let i = groups.length - 1; i >= 0; i--) {
      const groupKey = groups[i];
      if (scrollTop >= this.groupPositions[groupKey] - 50) {
        if (this.activeGroup !== groupKey) {
          this.activeGroup = groupKey;
        }
        break;
      }
    }
  }

  // 重置滚动位置
  resetScrollPosition() {
    const container = this.$refs.fieldsContainer as HTMLElement;
    if (container) {
      container.scrollTop = 0;
    }
  }

  // 确认导出
  async handleConfirm() {
    try {
      // 表单验证
      await (this.$refs.form as any).validate();

      // 检查是否选择了字段
      if (this.selectedFields.length === 0) {
        this.$message.error("请至少选择一个导出字段");
        return;
      }

      // 检查候选人ID列表
      const candidateIds = this.getCandidateIds();
      // if (candidateIds.length === 0) {
      //   this.$message.error("请先选择要导出的候选人");
      //   return;
      // }

      this.confirmLoading = true;

      // 构建导出参数
      const params = this.buildBaseParams();
      params.execType = 0; // 发起导出任务
      params.taskName = this.formData.fileName.trim();
      params.suffix = this.formData.fileType;
      params.headersInSql = this.selectedFields.map(field => field.headerInSql);

      const response = await ExportApi.newTaskCand(params);

      if (response && response.exportId && response.exportId > 0) {
        // 导出成功，保存字段选择和排序到缓存
        const exportFields = this.selectedFields.map(field => field.headerInSql);
        this.saveFieldsCache(exportFields);
        this.saveFieldsOrderCache(exportFields);

        this.$confirm("导出任务创建成功", "提示", {
          confirmButtonText: "查看",
          cancelButtonText: "取消",
          type: "success"
        })
          .then(() => {
            this.$router.push(`/export/list?companyId=${Auth.companyId}`);
          })
          .catch(() => {
            // 取消查看，什么都不做
          });
        this.handleClose();
      } else {
        this.$message.error("创建导出任务失败，请重试");
      }
    } catch (error) {
      console.error("导出失败:", error);
      if (error && error.message) {
        this.$message.error(`导出失败: ${error.message}`);
      } else {
        this.$message.error("导出失败，请稍后重试");
      }
    } finally {
      this.confirmLoading = false;
    }
  }

  @Emit("close")
  handleClose() {
    // 重置表单状态
    this.confirmLoading = false;
    this.leftSearchKey = "";
    this.rightSearchKey = "";
    this.activeGroup = "";
    this.isClickScrolling = false;

    // 重置loading状态和进度文本
    this.clearAllLoading = false;
    this.groupToggleLoading = {};
    this.clearAllProgressText = "";
    this.groupProgressText = {};

    // 重置表单数据
    this.formData = {
      fileName: "",
      fileType: "xlsx"
    };

    // 清除表单验证错误
    if (this.$refs.form) {
      (this.$refs.form as any).clearValidate();
    }

    // 重置滚动位置
    this.resetScrollPosition();

    return;
  }
}
